import { io, type Socket } from 'socket.io-client';
import type { AckResult, ClientToServerEvents, ServerToClientEvents } from '@kokun/shared';
import { Store } from '../app/store';

export type ConnStatus = 'offline' | 'connecting' | 'online' | 'reconnecting' | 'error';

export interface ConnState {
  status: ConnStatus;
  rtt: number;
  error: string | null;
}

export type KSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 * Conexión Socket.IO con reconexión automática y estimación del reloj del servidor
 * (offset calculado con el ping de menor latencia de una ventana deslizante).
 */
export class Connection {
  readonly state = new Store<ConnState>({ status: 'offline', rtt: 0, error: null });
  socket: KSocket | null = null;
  private offset = 0;
  private samples: Array<{ rtt: number; offset: number }> = [];
  private pingTimer: number | null = null;

  /** Hora estimada del servidor en ms. */
  serverNow(): number {
    return Date.now() + this.offset;
  }

  connect(token: string) {
    this.disconnect();
    this.state.set({ status: 'connecting', error: null });
    const s: KSocket = io({ path: '/socket.io', auth: { token }, transports: ['websocket', 'polling'], reconnectionDelay: 800, reconnectionDelayMax: 5000 });
    this.socket = s;
    s.on('connect', () => {
      this.state.set({ status: 'online', error: null });
      this.ping();
    });
    s.on('disconnect', (reason) => {
      this.state.set({ status: reason === 'io client disconnect' ? 'offline' : 'reconnecting' });
    });
    s.on('connect_error', (err) => {
      this.state.set({ status: 'error', error: err.message });
    });
    s.io.on('reconnect_attempt', () => this.state.set({ status: 'reconnecting' }));
    this.pingTimer = window.setInterval(() => this.ping(), 2000);
  }

  private ping() {
    const s = this.socket;
    if (!s?.connected) return;
    const c = Date.now();
    s.emit('time:ping', { c }, (r) => {
      const now = Date.now();
      const rtt = now - r.c;
      this.samples.push({ rtt, offset: r.s + rtt / 2 - now });
      if (this.samples.length > 8) this.samples.shift();
      const best = [...this.samples].sort((a, b) => a.rtt - b.rtt)[0];
      this.offset = best.offset;
      this.state.set({ rtt: Math.round(this.samples.reduce((a, b) => a + b.rtt, 0) / this.samples.length) });
    });
  }

  /** Emite una petición con acknowledgement y la convierte en promesa. */
  request<T>(event: keyof ClientToServerEvents, payload: unknown = {}, timeout = 8000): Promise<T> {
    return new Promise((resolve, reject) => {
      const s = this.socket;
      if (!s?.connected) return reject(new Error('Sin conexión con el servidor.'));
      const timer = setTimeout(() => reject(new Error('El servidor no responde.')), timeout);
      (s.emit as (e: string, p: unknown, ack: (r: AckResult<T>) => void) => void)(event, payload, (r) => {
        clearTimeout(timer);
        if (r.ok) resolve(r.data);
        else reject(new Error(r.error));
      });
    });
  }

  emit(event: keyof ClientToServerEvents, payload: unknown = {}) {
    (this.socket?.emit as ((e: string, p: unknown) => void) | undefined)?.call(this.socket, event, payload);
  }

  disconnect() {
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    this.socket?.removeAllListeners();
    this.socket?.disconnect();
    this.socket = null;
    this.state.set({ status: 'offline' });
  }
}

export const connection = new Connection();
